import { splitEncoding } from '@contentful/live-preview';

import { SUNRUN_URL } from '@/constants/baseUrls';
import type { Seo } from '@/contentful/contentful-exports';
import type { ParsedSolarByStatePath } from '@/utils/parseSolarByStatePath';
import usStates from '@/utils/usStates';

export type AreaServed =
  | { '@type': 'State'; name: string }
  | {
      '@type': 'City';
      name: string;
      containedInPlace: { '@type': 'State'; name: string };
    };

export const SUNRUN_ORGANIZATION = {
  '@type': 'Organization' as const,
  name: 'Sunrun',
  url: 'https://www.sunrun.com/',
};

export function resolveGeoPageUrl(path: string, seoFields?: Seo): string {
  return seoFields?.fields?.canonicalUrl ?? `${SUNRUN_URL}${path}`;
}

export function buildAreaServed(
  parsed: ParsedSolarByStatePath
): AreaServed | null {
  if (parsed.pageType === 'state') {
    return {
      '@type': 'State',
      name: parsed.stateName,
    };
  }

  // Never invent a City from the state name — that produces false local entities.
  if (!parsed.cityName) {
    return null;
  }

  return {
    '@type': 'City',
    name: parsed.cityName,
    containedInPlace: {
      '@type': 'State',
      name: parsed.stateName,
    },
  };
}

/** Accepts either a state code ("CA") or a full state name ("California"). */
function resolveStateOverride(
  state?: string
): Pick<ParsedSolarByStatePath, 'stateCode' | 'stateName'> | null {
  if (!state) return null;

  const asCode = state.toUpperCase();
  if (usStates[asCode]) {
    return { stateCode: asCode, stateName: usStates[asCode] };
  }

  const byName = Object.entries(usStates).find(
    ([, name]) => name.toLowerCase() === state.toLowerCase()
  );

  return byName ? { stateCode: byName[0], stateName: byName[1] } : null;
}

/**
 * CMS values win over the path/metaTitle parser so editors can correct a
 * mis-parsed market. Blank fields fall back to the parsed location.
 */
export function applyGeoLocationOverrides(
  parsed: ParsedSolarByStatePath,
  seoFields?: Seo
): ParsedSolarByStatePath {
  const { structuredDataCity, structuredDataState } = seoFields?.fields ?? {};

  const cityOverride = splitEncoding(structuredDataCity || '').cleaned.trim();
  const stateOverride = resolveStateOverride(
    splitEncoding(structuredDataState || '').cleaned.trim()
  );

  return {
    ...parsed,
    ...stateOverride,
    // pageType is deliberately left alone: state pages ignore cityName
    // downstream, so a stray city can't recategorize a statewide URL.
    cityName: cityOverride || parsed.cityName,
  };
}

export function buildServiceLocationLabel(
  parsed: ParsedSolarByStatePath
): string | null {
  if (parsed.pageType === 'state') {
    return parsed.stateName;
  }

  if (!parsed.cityName) {
    return null;
  }

  return `${parsed.cityName}, ${parsed.stateCode}`;
}
